import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import api, { eur, formatApiError } from "@/lib/api";

/* ---------- Merken ---------- */
export const AdminBrands = () => {
  const [brands, setBrands] = useState([]);
  const [form, setForm] = useState({ name: "", tagline: "" });
  const [error, setError] = useState("");

  const load = () => api.get("/brands").then((d) => setBrands(d.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/admin/brands", { ...form, order: brands.length + 1 });
      setForm({ name: "", tagline: "" });
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-brands">
      <h1 className="font-serif font-light text-cream text-3xl mb-8">Merken (categorieën)</h1>
      <form data-testid="admin-brand-form" onSubmit={create} className="flex flex-wrap gap-3 mb-8">
        <input data-testid="admin-brand-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Merknaam" className="bg-ink border border-gold/15 px-4 py-2.5 text-sm text-cream" />
        <input data-testid="admin-brand-tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Korte slogan" className="flex-1 min-w-48 bg-ink border border-gold/15 px-4 py-2.5 text-sm text-cream" />
        <button type="submit" data-testid="admin-brand-save" className="bg-gold text-obsidian font-mono text-[10px] tracking-[0.25em] uppercase px-6">Toevoegen</button>
      </form>
      {error && <p className="text-xs text-red-400 mb-4">{error}</p>}
      <div className="border border-gold/15 bg-ink">
        {brands.map((b) => (
          <div key={b.slug} className="flex items-center justify-between border-b border-gold/5 p-4">
            <div>
              <p className="text-cream">{b.name} <span className="font-mono text-[9px] text-smoke ml-2">{b.slug} · {b.product_count} producten</span></p>
              <p className="text-xs text-mist mt-0.5">{b.tagline}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- Kortingscodes ---------- */
export const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ code: "", discount_type: "percent", value: 10, min_total: 0, active: true });
  const [error, setError] = useState("");

  const load = () => api.get("/admin/coupons").then((d) => setCoupons(d.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/admin/coupons", { ...form, value: parseFloat(form.value), min_total: parseFloat(form.min_total) });
      setForm({ code: "", discount_type: "percent", value: 10, min_total: 0, active: true });
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const toggle = async (c) => {
    await api.put(`/admin/coupons/${c.code}`, { code: c.code, discount_type: c.discount_type, value: c.value, min_total: c.min_total, active: !c.active });
    load();
  };

  const remove = async (code) => {
    if (!window.confirm(`Code ${code} verwijderen?`)) return;
    await api.delete(`/admin/coupons/${code}`);
    load();
  };

  return (
    <div data-testid="admin-coupons">
      <h1 className="font-serif font-light text-cream text-3xl mb-8">Kortingscodes</h1>
      <form data-testid="admin-coupon-form" onSubmit={create} className="flex flex-wrap gap-3 mb-8 items-end">
        <label className="block">
          <span className="font-mono text-[9px] uppercase text-smoke block mb-1.5">Code</span>
          <input data-testid="admin-coupon-code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="ZOMER20" className="bg-ink border border-gold/15 px-4 py-2.5 text-sm text-cream w-36" />
        </label>
        <label className="block">
          <span className="font-mono text-[9px] uppercase text-smoke block mb-1.5">Type</span>
          <select data-testid="admin-coupon-type" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="bg-ink border border-gold/15 px-4 py-2.5 text-sm text-cream">
            <option value="percent">Procent (%)</option>
            <option value="fixed">Vastbedrag (€)</option>
          </select>
        </label>
        <label className="block">
          <span className="font-mono text-[9px] uppercase text-smoke block mb-1.5">Waarde</span>
          <input data-testid="admin-coupon-value" required type="number" step="0.5" min="0" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="bg-ink border border-gold/15 px-4 py-2.5 text-sm text-cream w-24" />
        </label>
        <label className="block">
          <span className="font-mono text-[9px] uppercase text-smoke block mb-1.5">Min. bestelling (€)</span>
          <input data-testid="admin-coupon-min" type="number" step="0.5" min="0" value={form.min_total} onChange={(e) => setForm({ ...form, min_total: e.target.value })} className="bg-ink border border-gold/15 px-4 py-2.5 text-sm text-cream w-32" />
        </label>
        <button type="submit" data-testid="admin-coupon-save" className="bg-gold text-obsidian font-mono text-[10px] tracking-[0.25em] uppercase px-6 py-2.5">Toevoegen</button>
      </form>
      {error && <p className="text-xs text-red-400 mb-4">{error}</p>}
      <div className="border border-gold/15 bg-ink">
        {coupons.map((c) => (
          <div key={c.code} className="flex flex-wrap items-center justify-between gap-3 border-b border-gold/5 p-4">
            <div>
              <p className="font-mono text-sm text-gold">{c.code}</p>
              <p className="font-mono text-[10px] text-smoke mt-1">{c.discount_type === "percent" ? `${c.value}% korting` : `€ ${c.value} korting`} · min. € {c.min_total} · {c.used_count}× gebruikt</p>
            </div>
            <div className="flex items-center gap-4">
              <button data-testid={`coupon-toggle-${c.code}`} onClick={() => toggle(c)} className={`font-mono text-[9px] uppercase border px-3 py-1.5 ${c.active ? "text-emerald-300 border-emerald-300/30" : "text-red-400 border-red-400/30"}`}>
                {c.active ? "Actief" : "Inactief"}
              </button>
              <button onClick={() => remove(c.code)} aria-label="Verwijderen" className="text-smoke hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- Klanten (accounts beheren) ---------- */
export const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/customers").then((d) => setCustomers(d.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setRole = async (c) => {
    setError("");
    try {
      const next = c.role === "admin" ? "customer" : "admin";
      await api.put(`/admin/users/${c.user_id}/role`, { role: next });
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const removeUser = async (c) => {
    if (!window.confirm(`Account "${c.email}" echt verwijderen? Bestellingen blijven behouden.`)) return;
    setError("");
    try {
      await api.delete(`/admin/users/${c.user_id}`);
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-customers">
      <h1 className="font-serif font-light text-cream text-3xl mb-8">Klanten <span className="text-smoke text-lg">({customers.length})</span></h1>
      {error && <p data-testid="admin-customers-error" className="text-xs text-red-400 mb-4">{error}</p>}
      <div className="border border-gold/15 bg-ink overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="border-b border-gold/15 text-left font-mono text-[9px] tracking-[0.2em] uppercase text-smoke">
              <th className="p-4">Naam</th><th className="p-4">E-mail</th><th className="p-4">Inlog</th><th className="p-4">Sinds</th><th className="p-4">Adressen</th><th className="p-4">Bestellingen</th><th className="p-4">Besteed</th><th className="p-4">Rol</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.user_id} data-testid={`customer-row-${c.user_id}`} className="border-b border-gold/5">
                <td className="p-4 text-cream">{c.name}</td>
                <td className="p-4 text-mist">{c.email}</td>
                <td className="p-4 font-mono text-[10px] text-smoke uppercase">{c.password_hash ? "E-mail" : "Google"}</td>
                <td className="p-4 font-mono text-[10px] text-smoke">{(c.created_at || "").slice(0, 10)}</td>
                <td className="p-4 font-mono text-cream">{(c.addresses || []).length}</td>
                <td className="p-4 font-mono text-cream">{c.orders_count}</td>
                <td className="p-4 font-serif text-gold">{eur(c.total_spent)}</td>
                <td className="p-4">
                  <button
                    data-testid={`customer-role-toggle-${c.user_id}`}
                    onClick={() => setRole(c)}
                    className={`font-mono text-[9px] uppercase border px-3 py-1.5 transition-colors ${
                      c.role === "admin" ? "text-gold border-gold/40 bg-gold/10" : "text-mist border-cream/15 hover:border-gold/40 hover:text-gold"
                    }`}
                  >
                    {c.role === "admin" ? "Admin" : "Klant"}
                  </button>
                </td>
                <td className="p-4">
                  <button data-testid={`customer-delete-${c.user_id}`} onClick={() => removeUser(c)} aria-label="Account verwijderen" className="text-smoke hover:text-red-400 transition-colors">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {customers.length === 0 && <tr><td colSpan={9} className="p-6 text-mist">Nog geen klanten.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ---------- Reviews ---------- */
export const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const load = () => api.get("/admin/reviews").then((d) => setReviews(d.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setApproved = async (id, approved) => { await api.put(`/admin/reviews/${id}?approved=${approved}`); load(); };
  const remove = async (id) => { await api.delete(`/admin/reviews/${id}`); load(); };

  return (
    <div data-testid="admin-reviews">
      <h1 className="font-serif font-light text-cream text-3xl mb-8">Reviews beheren</h1>
      <div className="space-y-3">
        {reviews.map((r) => (
          <div key={r.review_id} data-testid={`admin-review-${r.review_id}`} className="flex flex-wrap items-center justify-between gap-4 border border-gold/15 bg-ink p-5">
            <div className="min-w-0">
              <p className="font-mono text-[10px] text-smoke">{r.product_model} · {r.user_name} · {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</p>
              <p className="text-sm text-mist mt-2">{r.text}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button data-testid={`review-toggle-${r.review_id}`} onClick={() => setApproved(r.review_id, !r.approved)} className={`font-mono text-[9px] uppercase border px-3 py-1.5 ${r.approved ? "text-emerald-300 border-emerald-300/30" : "text-red-400 border-red-400/30"}`}>
                {r.approved ? "Zichtbaar" : "Verborgen"}
              </button>
              <button onClick={() => remove(r.review_id)} aria-label="Verwijderen" className="text-smoke hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {reviews.length === 0 && <p className="text-sm text-mist">Nog geen reviews.</p>}
      </div>
    </div>
  );
};

/* ---------- Retouren ---------- */
export const AdminReturns = () => {
  const [returns, setReturns] = useState([]);
  const load = () => api.get("/admin/returns").then((d) => setReturns(d.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => { await api.put(`/admin/returns/${id}?status=${status}`); load(); };

  return (
    <div data-testid="admin-returns">
      <h1 className="font-serif font-light text-cream text-3xl mb-8">Retouren & ruilingen</h1>
      <div className="space-y-3">
        {returns.map((r) => (
          <div key={r.return_id} data-testid={`admin-return-${r.return_id}`} className="flex flex-wrap items-center justify-between gap-4 border border-gold/15 bg-ink p-5">
            <div>
              <p className="font-mono text-xs text-gold">{r.order_id} · € {(r.order_total || 0).toFixed(2).replace(".", ",")}</p>
              <p className="text-sm text-mist mt-2 max-w-xl">{r.reason}</p>
              <p className="font-mono text-[10px] text-smoke mt-2">{r.user_name} · {(r.created_at || "").slice(0, 10)}</p>
            </div>
            <select
              data-testid={`return-status-${r.return_id}`}
              value={r.status}
              onChange={(e) => setStatus(r.return_id, e.target.value)}
              className="bg-obsidian border border-gold/15 px-4 py-2.5 text-sm text-cream"
            >
              {["requested", "approved", "rejected", "completed"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        ))}
        {returns.length === 0 && <p className="text-sm text-mist">Geen retouraanvragen.</p>}
      </div>
    </div>
  );
};

/* ---------- Berichten ---------- */
export const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const load = () => api.get("/admin/messages").then((d) => setMessages(d.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setHandled = async (id, handled) => { await api.put(`/admin/messages/${id}?handled=${handled}`); load(); };

  return (
    <div data-testid="admin-messages">
      <h1 className="font-serif font-light text-cream text-3xl mb-8">Contactberichten</h1>
      <div className="space-y-3">
        {messages.map((m) => (
          <div key={m.message_id} data-testid={`admin-message-${m.message_id}`} className={`border p-5 ${m.handled ? "border-gold/5 bg-ink/50 opacity-60" : "border-gold/20 bg-ink"}`}>
            <div className="flex items-center justify-between gap-4">
              <p className="font-mono text-xs text-gold">{m.subject}</p>
              <button data-testid={`message-toggle-${m.message_id}`} onClick={() => setHandled(m.message_id, !m.handled)} className="font-mono text-[9px] uppercase border border-gold/30 text-mist px-3 py-1.5 hover:text-gold transition-colors">
                {m.handled ? "Afgehandeld" : "Markeren als afgehandeld"}
              </button>
            </div>
            <p className="text-sm text-mist mt-3">{m.message}</p>
            <p className="font-mono text-[10px] text-smoke mt-3">{m.name} · {m.email} · {(m.created_at || "").slice(0, 10)}</p>
          </div>
        ))}
        {messages.length === 0 && <p className="text-sm text-mist">Geen berichten.</p>}
      </div>
    </div>
  );
};
